import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import axios from 'axios';

const defaultFilters = {
  module: 'NOTIFICATIONS',
  action: '',
  status: '',
  entityType: '',
  userId: '',
  branchId: '',
  search: '',
  from: '',
  to: '',
  page: 1,
  limit: 25,
};

export default function AuditLogs() {
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => setFilters({ ...defaultFilters });

  const fetchLogs = async (opts = {}) => {
    setLoading(true);
    try {
      const q = { ...filters, ...opts };
      const params = new URLSearchParams();
      Object.entries(q).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          params.append(k, v);
        }
      });
      const { data } = await axios.get(`/api/audit?${params.toString()}`);
      if (data?.success) {
        setRows(data.data || []);
        setPagination(data.pagination || { page: q.page || 1, limit: q.limit || 25, total: 0, pages: 0 });
      }
    } catch (err) {
      // no-op
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchLogs({ page: 1 });
  };

  const handlePageChange = (_, page) => {
    setFilters(prev => ({ ...prev, page }));
    fetchLogs({ page });
  };

  const statusColor = useMemo(() => ({ SUCCESS: '#2e7d32', FAIL: '#c62828', INFO: '#0277bd' }), []);

  const handleRowClick = (row) => {
    setSelected(row);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelected(null);
  };

  const exportCsv = () => {
    const headers = ['Time','User','Branch','Module','Action','Status','Message','EntityType','EntityId','IP','UserAgent'];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      const q = s.replace(/"/g, '""');
      return '"' + q + '"';
    };
    const lines = [headers.join(',')];
    (rows || []).forEach(r => {
      const line = [
        new Date(r.createdAt).toLocaleString(),
        r.userName || r.userId || '',
        r.branchName || r.branchId || '',
        r.module || '',
        r.action || '',
        r.status || '',
        r.message || '',
        r.entityType || '',
        r.entityId || '',
        r.ip || '',
        r.userAgent || ''
      ].map(escape).join(',');
      lines.push(line);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audit_logs.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        Audit Logs
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Module</InputLabel>
              <Select label="Module" value={filters.module} onChange={e => handleChange('module', e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="NOTIFICATIONS">Notifications</MenuItem>
                <MenuItem value="HRM">HRM</MenuItem>
                <MenuItem value="POS">POS</MenuItem>
                <MenuItem value="ACCOUNTS">Accounts</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Action" value={filters.action} onChange={e => handleChange('action', e.target.value)} placeholder="CONFIG_UPDATE / EMAIL_TEST / SMS_TEST" />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="User (ID)" value={filters.userId} onChange={e => handleChange('userId', e.target.value)} placeholder="User ObjectId" />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Branch (ID/Code)" value={filters.branchId} onChange={e => handleChange('branchId', e.target.value)} placeholder="Branch ObjectId / Code" />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={filters.status} onChange={e => handleChange('status', e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                <MenuItem value="FAIL">FAIL</MenuItem>
                <MenuItem value="INFO">INFO</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Entity Type" value={filters.entityType} onChange={e => handleChange('entityType', e.target.value)} placeholder="NotificationConfig" />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField fullWidth type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from} onChange={e => handleChange('from', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to} onChange={e => handleChange('to', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Search" value={filters.search} onChange={e => handleChange('search', e.target.value)} placeholder="Message / Action / Type" />
          </Grid>
          <Grid item xs={12} md={2} display="flex" alignItems="center" gap={1}>
            <Button fullWidth variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading}>Search</Button>
          </Grid>
          <Grid item xs={12} md={2} display="flex" alignItems="center" gap={1}>
            <Button fullWidth variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchLogs()} disabled={loading}>Refresh</Button>
          </Grid>
          <Grid item xs={12} md={2} display="flex" alignItems="center" gap={1}>
            <Button fullWidth variant="text" onClick={resetFilters} disabled={loading}>Reset</Button>
          </Grid>
          <Grid item xs={12} md={3} display="flex" alignItems="center" gap={1}>
            <Button fullWidth variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportCsv} disabled={loading || rows.length === 0}>Export CSV</Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row._id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(row)}>
                <TableCell>{new Date(row.createdAt).toLocaleString()}</TableCell>
                <TableCell>{row.userName || '-'}</TableCell>
                <TableCell>{row.branchName || '-'}</TableCell>
                <TableCell>{row.module}</TableCell>
                <TableCell>{row.action}</TableCell>
                <TableCell sx={{ color: statusColor[row.status] || 'inherit', fontWeight: 600 }}>{row.status}</TableCell>
                <TableCell>{row.message}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">{loading ? 'Loading...' : 'No records found'}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Pagination
            color="primary"
            page={pagination.page || 1}
            count={pagination.pages || 1}
            onChange={handlePageChange}
          />
        </Box>
      </Paper>

      <Dialog open={detailsOpen} onClose={closeDetails} fullWidth maxWidth="md">
        <DialogTitle>Audit Log Details</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><Typography variant="body2"><strong>Time:</strong> {new Date(selected.createdAt).toLocaleString()}</Typography></Grid>
                <Grid item xs={12} md={6}><Typography variant="body2"><strong>Status:</strong> {selected.status}</Typography></Grid>
                <Grid item xs={12} md={6}><Typography variant="body2"><strong>User:</strong> {selected.userName || selected.userId || '-'}</Typography></Grid>
                <Grid item xs={12} md={6}><Typography variant="body2"><strong>Branch:</strong> {selected.branchName || selected.branchId || '-'}</Typography></Grid>
                <Grid item xs={12} md={6}><Typography variant="body2"><strong>Module:</strong> {selected.module}</Typography></Grid>
                <Grid item xs={12} md={6}><Typography variant="body2"><strong>Action:</strong> {selected.action}</Typography></Grid>
                <Grid item xs={12} md={6}><Typography variant="body2"><strong>Entity:</strong> {selected.entityType || '-'} {selected.entityId ? `(${selected.entityId})` : ''}</Typography></Grid>
                <Grid item xs={12} md={6}><Typography variant="body2"><strong>IP:</strong> {selected.ip || '-'}</Typography></Grid>
                <Grid item xs={12}><Typography variant="body2" sx={{ wordBreak: 'break-all' }}><strong>User Agent:</strong> {selected.userAgent || '-'}</Typography></Grid>
                <Grid item xs={12}><Typography variant="body2"><strong>Message:</strong> {selected.message || '-'}</Typography></Grid>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Details JSON:</strong></Typography>
                  <Box component="pre" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, maxHeight: 320, overflow: 'auto' }}>
                    {JSON.stringify(selected.details || {}, null, 2)}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
